import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Upload, 
  FileText, 
  Building2, 
  Mail, 
  Phone, 
  MapPin,
  Download,
  X
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { clientsAPI, documentsAPI } from '@/lib/api';
import { toast } from 'sonner';

const documentCategories = [
  { value: 'registration', label: 'Registration Documents' },
  { value: 'tax', label: 'Tax Documents' },
  { value: 'financial', label: 'Financial Documents' },
  { value: 'other', label: 'Other' },
];

export default function AdminClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [client, setClient] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadData, setUploadData] = useState({
    name: '',
    category: 'other',
    file: null,
  });

  useEffect(() => {
    fetchData();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [clientRes, docsRes] = await Promise.all([
        clientsAPI.getOne(id),
        documentsAPI.getByClient(id),
      ]);
      setClient(clientRes.data);
      setDocuments(docsRes.data);
    } catch (error) {
      toast.error('Failed to load client');
      navigate('/admin/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this client? All associated documents will also be deleted.')) return;
    try {
      await clientsAPI.delete(id);
      toast.success('Client deleted successfully');
      navigate('/admin/clients');
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadData({ ...uploadData, file, name: uploadData.name || file.name });
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file || !uploadData.name) {
      toast.error('Please select a file and provide a name');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadData.file);
    formData.append('name', uploadData.name);
    formData.append('category', uploadData.category);
    formData.append('client_id', id);

    try {
      await documentsAPI.upload(formData);
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      setUploadData({ name: '', category: 'other', file: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await documentsAPI.delete(docId);
      setDocuments(documents.filter(d => d.id !== docId));
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await documentsAPI.getOne(doc.id);
      const fileData = response.data.file_data;
      const byteCharacters = atob(fileData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: doc.file_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-client-detail">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <Link to="/admin/clients" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4">
            <ArrowLeft size={18} />
            Back to Clients
          </Link>
          <h1 className="text-2xl font-bold text-slate-900">{client.company_name}</h1>
          <span className={`badge mt-2 ${client.client_type === 'micro_business' ? 'badge-micro' : 'badge-nonprofit'}`}>
            {client.client_type === 'micro_business' ? 'Micro Business' : 'Non-Profit'}
          </span>
        </div>
        <div className="flex gap-3">
          <Link to={`/admin/clients/${id}/edit`}>
            <Button variant="outline" data-testid="edit-client-btn">
              <Edit size={18} className="mr-2" />
              Edit
            </Button>
          </Link>
          <Button variant="outline" className="text-red-600 hover:bg-red-50" onClick={handleDelete} data-testid="delete-client-btn">
            <Trash2 size={18} className="mr-2" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client Info */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Client Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="text-slate-900">{client.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-sm text-slate-500">Phone</p>
                <p className="text-slate-900">{client.phone}</p>
              </div>
            </div>
            {client.address && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Address</p>
                  <p className="text-slate-900">{client.address}</p>
                </div>
              </div>
            )}
            <div className="pt-4 border-t border-slate-200 space-y-3">
              <div>
                <p className="text-sm text-slate-500">Contact Person</p>
                <p className="font-medium text-slate-900">{client.contact_person}</p>
              </div>
              {client.turnover && (
                <div>
                  <p className="text-sm text-slate-500">Annual Turnover</p>
                  <p className="font-medium text-slate-900">{client.turnover}</p>
                </div>
              )}
              {client.pan && (
                <div>
                  <p className="text-sm text-slate-500">PAN</p>
                  <p className="font-medium text-slate-900">{client.pan}</p>
                </div>
              )}
              {client.gst && (
                <div>
                  <p className="text-sm text-slate-500">GST</p>
                  <p className="font-medium text-slate-900">{client.gst}</p>
                </div>
              )}
            </div>
            {client.notes && (
              <div className="pt-4 border-t border-slate-200">
                <p className="text-sm text-slate-500 mb-1">Notes</p>
                <p className="text-slate-700 text-sm">{client.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Documents */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Documents ({documents.length})
            </CardTitle>
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#E84C3D] hover:bg-[#C0392B]" data-testid="upload-document-btn">
                  <Upload size={18} className="mr-2" />
                  Upload
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Upload Document</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Document Name</Label>
                    <Input
                      value={uploadData.name}
                      onChange={(e) => setUploadData({ ...uploadData, name: e.target.value })}
                      placeholder="e.g., GST Certificate"
                      data-testid="document-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={uploadData.category} onValueChange={(v) => setUploadData({ ...uploadData, category: v })}>
                      <SelectTrigger data-testid="document-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {documentCategories.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>File</Label>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-slate-300 transition-colors"
                      data-testid="file-upload-area"
                    >
                      {uploadData.file ? (
                        <div className="flex items-center justify-center gap-2">
                          <FileText className="w-5 h-5 text-[#E84C3D]" />
                          <span className="text-slate-700">{uploadData.file.name}</span>
                          <button onClick={(e) => { e.stopPropagation(); setUploadData({ ...uploadData, file: null }); }}>
                            <X size={16} className="text-slate-400 hover:text-slate-600" />
                          </button>
                        </div>
                      ) : (
                        <div>
                          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-slate-600">Click to select a file</p>
                          <p className="text-sm text-slate-400 mt-1">PDF, DOC, XLS, Images</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button 
                    onClick={handleUpload} 
                    className="w-full bg-[#E84C3D] hover:bg-[#C0392B]"
                    disabled={uploading}
                    data-testid="upload-submit-btn"
                  >
                    {uploading ? 'Uploading...' : 'Upload Document'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {documents.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-4">No documents uploaded yet</p>
                <Button onClick={() => setUploadDialogOpen(true)} variant="outline">
                  Upload First Document
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div 
                    key={doc.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                    data-testid={`document-${doc.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-[#E84C3D]" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{doc.name}</p>
                        <p className="text-sm text-slate-500 capitalize">{doc.category}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)} data-testid={`download-${doc.id}`}>
                        <Download size={16} />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteDocument(doc.id)}
                        data-testid={`delete-doc-${doc.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
